import {useCallback} from "react";
import {useTranslation} from "react-i18next";

const en = {
    ALTERNATIVES: "Suggested alternatives",
    ALTERNATIVES_HELP: "Quantities are converted for each alternative. Open a row for details.",
    ALL: "All",
    FULL: "Full coverage",
    PARTIAL: "Partial coverage",
    AVAILABLE: "Available",
    REQUESTED: "Requested",
    REQUIRED: "Needed",
    ALTERNATIVE: "Alternative",
    NO_STOCK_DATA: "No stock data for the selected warehouses",
    OUT_OF_STOCK: "Out of stock",
    SEARCH: "Find an article…",
    CART_SEARCH: "Search by article or description",
    CART_SEARCH_TITLE: "Search",
    CART_SEARCH_NOTICE: "Only items matching your search are shown.",
    CART_SEARCH_CLEAR_HINT: "Clear the search field to view the entire cart.",
    CART_NO_MATCHES: "No matching items in the cart",
    CART_EMPTY: "Your cart is empty",
    CART_EMPTY_HELP: "Add an item or upload a file below to get started.",
    CART_ADD_ITEM: "Add item",
    NO_MATCHES: "No matching alternatives",
    RANGE: "{{from}}–{{to}} of {{total}}",
    PCS: "Pack",
    KIT: "Kit",
    REPLACEMENT: "Replacement",
    PCS_HELP: "The same part in a different pack size.",
    KIT_HELP: "The requested part is included in this kit.",
    REPLACEMENT_HELP: "An equivalent replacement for the requested article.",
    RATIO: "1 × {{alternative}} replaces {{factor}} × {{original}}.",
    PARTIAL_EXPLANATION: "To replace {{requested}} × {{original}}, you need {{required}} × {{alternative}}. Only {{available}} are available.",
    FULL_EXPLANATION: "{{required}} × {{alternative}} covers your request for {{requested}} × {{original}}.",
    DETAILS: "Details for {{article}}",
    ADD: "Add {{qty}} × {{article}}",
    ADD_FAILED: "Could not update the cart. Please check the cart before trying again.",
    PARTIAL_TITLE: "Add a partial replacement?",
    PARTIAL_QUESTION: "Add only the available quantity and continue?",
    CONFIRM_ADD: "Yes, add {{qty}}",
    WAREHOUSE: "Warehouse",
};

const ru: Record<keyof typeof en, string> = {
    ALTERNATIVES: "Варианты замены",
    ALTERNATIVES_HELP: "Количество пересчитано для каждой замены. Подробности — в раскрытии строки.",
    ALL: "Все",
    FULL: "Полное покрытие",
    PARTIAL: "Частичное покрытие",
    AVAILABLE: "В наличии",
    REQUESTED: "Запрошено",
    REQUIRED: "Для замены",
    ALTERNATIVE: "Альтернатива",
    NO_STOCK_DATA: "Нет данных по выбранным складам",
    OUT_OF_STOCK: "Нет в наличии",
    SEARCH: "Найти артикул…",
    CART_SEARCH: "Поиск по артикулу или описанию",
    CART_SEARCH_TITLE: "Поиск",
    CART_SEARCH_NOTICE: "Отображаются только товары, соответствующие запросу.",
    CART_SEARCH_CLEAR_HINT: "Очистите поле поиска, чтобы увидеть всю корзину.",
    CART_NO_MATCHES: "В корзине ничего не найдено",
    CART_EMPTY: "Ваша корзина пуста",
    CART_EMPTY_HELP: "Добавьте товар или загрузите файл ниже, чтобы начать заказ.",
    CART_ADD_ITEM: "Добавить товар",
    NO_MATCHES: "Подходящих замен не найдено",
    RANGE: "{{from}}–{{to}} из {{total}}",
    PCS: "Упаковка",
    KIT: "Комплект",
    REPLACEMENT: "Замена",
    PCS_HELP: "Та же деталь в другой фасовке.",
    KIT_HELP: "Запрошенная деталь входит в состав этого комплекта.",
    REPLACEMENT_HELP: "Полноценная замена запрошенного артикула.",
    RATIO: "1 × {{alternative}} заменяет {{factor}} × {{original}}.",
    PARTIAL_EXPLANATION: "Для замены {{requested}} × {{original}} требуется {{required}} × {{alternative}}. Доступно только {{available}}.",
    FULL_EXPLANATION: "{{required}} × {{alternative}} полностью покрывает запрос {{requested}} × {{original}}.",
    DETAILS: "Подробнее об артикуле {{article}}",
    ADD: "Добавить {{qty}} × {{article}}",
    ADD_FAILED: "Не удалось обновить корзину. Проверьте её содержимое перед повторной попыткой.",
    PARTIAL_TITLE: "Добавить частичную замену?",
    PARTIAL_QUESTION: "Добавить только доступное количество и продолжить?",
    CONFIRM_ADD: "Да, добавить {{qty}}",
    WAREHOUSE: "Склад",
};

export type CartText = (key: keyof typeof en, values?: Record<string, string | number>) => string;

export const useCartText = (): CartText => {
    const {t, i18n} = useTranslation("translation", {useSuspense: false});
    const language = i18n.language;
    return useCallback((key, values) => String(t(`CART.UI.${key}`, {
        defaultValue: (language?.startsWith("ru") ? ru : en)[key],
        ...values,
    })), [t, language]);
};
